import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DocumentAnnotations = ({ annotations, onAddAnnotation, currentUser }) => {
  const [showAddComment, setShowAddComment] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [selectedPage, setSelectedPage] = useState(1);

  const handleAddComment = () => {
    if (newComment?.trim()) {
      onAddAnnotation({
        id: Date.now(),
        userId: currentUser?.id,
        userName: currentUser?.name,
        userAvatar: currentUser?.avatar,
        content: newComment,
        page: selectedPage,
        timestamp: new Date()?.toISOString(),
        type: 'comment'
      });
      setNewComment('');
      setShowAddComment(false);
    }
  };

  const groupedAnnotations = annotations?.reduce((acc, annotation) => {
    const page = annotation?.page || 1;
    if (!acc?.[page]) acc[page] = [];
    acc?.[page]?.push(annotation);
    return acc;
  }, {});

  const getAnnotationIcon = (type) => {
    switch (type) {
      case 'comment':
        return <Icon name="MessageCircle" size={16} className="text-accent" />;
      case 'highlight':
        return <Icon name="Highlighter" size={16} className="text-warning" />;
      case 'question':
        return <Icon name="HelpCircle" size={16} className="text-error" />;
      default:
        return <Icon name="MessageCircle" size={16} className="text-muted-foreground" />;
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);

    if (diffInHours < 1) {
      return `${Math.floor(diffInHours * 60)} min ago`;
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)} hours ago`;
    } else {
      return date?.toLocaleDateString();
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg h-full flex flex-col">
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-foreground">Comments & Notes</h3>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowAddComment(true)}
            iconName="Plus"
            iconPosition="left"
          >
            Add Comment
          </Button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto">
        {Object.keys(groupedAnnotations)?.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full p-8 text-center">
            <Icon name="MessageCircle" size={48} className="text-muted-foreground mb-4" />
            <h4 className="text-lg font-medium text-foreground mb-2">No comments yet</h4>
            <p className="text-sm text-muted-foreground mb-4">
              Add comments and notes to collaborate with other stakeholders during the review process.
            </p>
            <Button
              variant="outline"
              onClick={() => setShowAddComment(true)}
              iconName="Plus"
              iconPosition="left"
            >
              Add First Comment
            </Button>
          </div>
        ) : (
          <div className="p-4 space-y-6">
            {Object.entries(groupedAnnotations)?.sort(([a], [b]) => Number(a) - Number(b))?.map(([page, pageAnnotations]) => (
                <div key={page} className="space-y-3">
                  <div className="flex items-center space-x-2 pb-2 border-b border-border">
                    <Icon name="FileText" size={16} className="text-muted-foreground" />
                    <span className="text-sm font-medium text-foreground">Page {page}</span>
                    <span className="text-xs text-muted-foreground">
                      ({pageAnnotations?.length} comment{pageAnnotations?.length !== 1 ? 's' : ''})
                    </span>
                  </div>

                  {pageAnnotations?.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))?.map((annotation) => (
                      <div key={annotation?.id} className="flex space-x-3 p-3 bg-muted/30 rounded-lg">
                        <div className="flex-shrink-0">
                          <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                            <span className="text-xs font-medium text-primary-foreground">
                              {annotation?.userName?.charAt(0)}
                            </span>
                          </div>
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2 mb-1">
                            <span className="text-sm font-medium text-foreground">
                              {annotation?.userName}
                            </span>
                            {getAnnotationIcon(annotation?.type)}
                            <span className="text-xs text-muted-foreground">
                              {formatTimestamp(annotation?.timestamp)}
                            </span>
                          </div>
                          <p className="text-sm text-foreground">{annotation?.content}</p>

                          {annotation?.type === 'question' && (
                            <div className="mt-2 p-2 bg-warning/10 border border-warning/20 rounded">
                              <div className="flex items-center space-x-2">
                                <Icon name="AlertTriangle" size={14} className="text-warning" />
                                <span className="text-xs font-medium text-warning">
                                  Requires Response
                                </span>
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="flex-shrink-0">
                          <Button variant="ghost" size="icon" className="h-6 w-6">
                            <Icon name="MoreHorizontal" size={14} />
                          </Button>
                        </div>
                      </div>
                    ))}
                </div>
              ))}
          </div>
        )}
      </div>
      {/* Add Comment Modal */}
      {showAddComment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-lg w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-foreground">Add Comment</h3>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowAddComment(false)}
                >
                  <Icon name="X" size={20} />
                </Button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Page Number
                  </label>
                  <select
                    value={selectedPage}
                    onChange={(e) => setSelectedPage(Number(e?.target?.value))}
                    className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
                  >
                    {Array.from({ length: 12 }, (_, i) => (
                      <option key={i + 1} value={i + 1}>
                        Page {i + 1}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Comment
                  </label>
                  <textarea
                    placeholder="Enter your comment or question..."
                    value={newComment}
                    onChange={(e) => setNewComment(e?.target?.value)}
                    className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent h-24 resize-none"
                  />
                </div>

                <div className="flex space-x-3 pt-4">
                  <Button
                    variant="outline"
                    onClick={() => setShowAddComment(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="default"
                    onClick={handleAddComment}
                    disabled={!newComment?.trim()}
                    className="flex-1"
                  >
                    Add Comment
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentAnnotations;